const STORAGE_KEY = 'post_drafts';

export interface PostDraft {
  id: string;
  savedAt: number;
  text: string;
  titlePoem: string;
  typePost: 'post' | 'poll' | 'poem';
  pollQuestion: string;
  pollOptions: string[];
  multipleChoice: boolean;
  videoUrl: string | null;
}

function readAll(): PostDraft[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeAll(drafts: PostDraft[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
}

export function listDrafts(): PostDraft[] {
  return readAll().sort((a, b) => b.savedAt - a.savedAt);
}

export function saveDraft(draft: Omit<PostDraft, 'id' | 'savedAt'>): PostDraft {
  const drafts = readAll();
  const entry: PostDraft = {
    ...draft,
    id: String(Date.now()),
    savedAt: Date.now(),
  };
  // Cap at 10 drafts — drop the oldest
  const updated = [entry, ...drafts].slice(0, 10);
  writeAll(updated);
  return entry;
}

export function deleteDraft(id: string) {
  writeAll(readAll().filter(d => d.id !== id));
}

export function clearAllDrafts() {
  localStorage.removeItem(STORAGE_KEY);
}

/** Build a snapshot of the current editor state for saving */
export function buildDraftSnapshot(params: {
  text: string;
  titlePoem: string;
  typePost: 'post' | 'poll' | 'poem';
  pollQuestion: string;
  pollOptions: string[];
  multipleChoice: boolean;
  videoUrl: string | null;
}): Omit<PostDraft, 'id' | 'savedAt'> {
  return { ...params };
}

/** Returns true when there is meaningful content worth saving */
export function hasDraftContent(params: {
  text: string;
  titlePoem: string;
  pollQuestion: string;
  videoUrl: string | null;
}): boolean {
  return !!(params.text.trim() || params.titlePoem.trim() || params.pollQuestion.trim() || params.videoUrl);
}
